import { formatCurrency } from '~libs/dataUtils';
import {
  deriveBaseAvailableAmount,
  deriveCaseManagementWarning,
  deriveClientWarning,
  deriveFundingType,
  derivePricePlan,
  showCaseManagement,
} from '~support-at-home/lib/derive';
import {
  onChangeClient,
  onChangePackageSchedule,
  onChangeSahBudgets,
  onChangeStartDate,
} from '~support-at-home/lib/onchange';
import { validateDateRange, validatePricePlan } from '~support-at-home/lib/validate';

import CascadingSelectors from '~components/form-fields/inputs/CascadingSelectors';
import { Step } from '~components/wizard-form/props';
import BudgetSupplements from '~support-at-home/components/budgets/BudgetSupplements';
import FundingLevelAmounts from '~support-at-home/components/budgets/FundingLevelAmounts';
import WizardInputPackageSelector from '~weekly-planner/components/wizard/WizardInputPackageSelector';

import { getAll as getPricePlanRevisions } from '~support-at-home/actions/pricePlanRevisions';

import { clear as clearPricePlans } from '~support-at-home/reducers/pricePlanRevisions';

import { selectLookupClients } from '~main/selectors/clients/clients';
import { selectPricePlanRevisionOptions } from '~support-at-home/selectors/pricePlanRevisions';
import { selectPricePlans } from '~support-at-home/selectors/pricePlans';
import { lookupSahBudgets } from '~support-at-home/selectors/sahBudgets';

import BudgetReview from '../../components/budgets/BudgetReview';
import BudgetSections from '../../components/budgets/BudgetSections';

const newBudgetFields: Step[] = [
  {
    step: 1,
    title: 'Dates & Client',
    fields: {
      id: {
        type: 'hidden',
      },
      start_date: {
        type: 'date',
        field: 'start_date',
        caption: 'Start Date',
        required: true,
        onChange: onChangeStartDate,
        validate: validateDateRange,
        order: 100,
      },
      end_date: {
        type: 'date',
        field: 'end_date',
        caption: 'End Date',
        required: true,
        disabled: false,
        validate: validateDateRange,
        order: 200,
      },
      client_id: {
        type: 'select',
        field: 'client_id',
        caption: 'Client',
        required: true,
        deriveWarning: deriveClientWarning,
        order: 300,
        selectorOptions: selectLookupClients,
        onChange: onChangeClient,
      },
      package_fixed_id: {
        type: 'custom',
        field: 'package_fixed_id',
        caption: 'Client Package',
        component: WizardInputPackageSelector,
        dependencies: ['package_fixed_id', 'client_id', 'end_date'],
        onChange: onChangePackageSchedule,
        required: true,
        order: 400,
      },
    },
  },
  {
    step: 2,
    title: 'Price Plan & Amount',
    fields: {
      price_plan: {
        type: 'custom',
        caption: 'Price Plan',
        required: false,
        component: CascadingSelectors,
        cascadingSelectors: [
          {
            position: 1,
            id: 'price_plan_id',
            name: 'price_plan_id',
            selectorOptions: selectPricePlans,
            fKey: 'plan_id',
          },
          {
            position: 2,
            id: 'price_plan_revision_id',
            name: 'price_plan_revision_id',
            selectorActions: getPricePlanRevisions,
            selectorOptions: selectPricePlanRevisionOptions,
            reducerFunctions: { clear: clearPricePlans },
            label: 'Rev. ',
            selectTop: true,
          },
        ],
        deriveValue: derivePricePlan,
        validate: validatePricePlan,
        order: 100,
      },
      sah_budget: {
        type: 'select',
        field: 'client_sah_budget_id',
        caption: 'SAH Budget',
        required: false,
        selectorOptions: lookupSahBudgets,
        clearable: true,
        order: 200,
        noOptionsMessage: 'No SaH budgets found for selected client',
        onChange: onChangeSahBudgets,
      },
      funding_type: {
        type: 'readonly',
        field: 'funding_type_id',
        caption: 'Funding Type',
        deriveValue: deriveFundingType,
        order: 300,
      },
      total_amount: {
        type: 'currency',
        field: 'total_amount',
        caption: 'Total Amount',
        disabled: (values: Record<string, unknown>) => !!values.client_sah_budget_id,
        order: 400,
      },
      total_includes_case_management: {
        type: 'switch',
        field: 'total_includes_case_management',
        caption: 'Total includes Case Management?',
        show: showCaseManagement,
        deriveWarning: deriveCaseManagementWarning,
        order: 500,
      },
      funding_level_amounts: {
        type: 'custom',
        dependencies: ['start_date', 'end_date'],
        component: FundingLevelAmounts,
        show: showCaseManagement,
        colSize: 4,
        order: 700,
      },
    },
  },
  {
    step: 3,
    title: 'Supplements',
    fields: {
      base_available_amount: {
        type: 'readonly',
        caption: 'Base Available Budget',
        description: 'Available budget amount (including case management) before inclusion of supplements',
        deriveValue: (values) => formatCurrency(deriveBaseAvailableAmount(values)),
      },
      supplements: {
        type: 'custom',
        field: 'supplements',
        caption: 'Supplements',
        component: BudgetSupplements,
        dependencies: ['supplements', 'package_fixed_id'],
      },
    },
  },
  {
    step: 4,
    title: 'Sections & Items',
    fields: {
      sections: {
        type: 'custom',
        component: BudgetSections,
        dependencies: [
          'id',
          'sections',
          'price_plan_revision_id',
          'client_id',
          'package_fixed_id',
          'start_date',
          'end_date',
          'total_amount',
          'total_includes_case_management',
          'available_amount',
          'grand_total',
          'supplements',
          'ignore_sah_filters',
        ],
        colSize: 12,
        order: 100,
      },
    },
  },
  {
    step: 5,
    title: 'Review',
    fields: {
      review: {
        type: 'custom',
        field: 'sections',
        component: BudgetReview,
        dependencies: [
          'sections',
          'client_id',
          'price_plan_id',
          'price_plan_revision_id',
          'start_date',
          'end_date',
          'package_fixed_id',
          'total_amount',
          'total_includes_case_management',
          'available_amount',
          'grand_total',
          'supplements',
          'ignore_sah_filters',
        ],
        colSize: 12,
        order: 100,
      },
    },
  },
];

export default newBudgetFields;
